import { useState, useEffect } from "react";
import useTranslation from "../useTranslation";
import { useCompanies } from "../../context/CompanyContext";
import { getBases, updateBaseInDataBases, deleteBase } from "../../api/baseApi";
import { toast } from "react-toastify";

export const useBaseManagement = () => {
  const { t } = useTranslation("productManagerPage");
  const { companies, loading: companiesLoading } = useCompanies();

  // Base states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [bases, setBases] = useState([]);
  const [filteredBases, setFilteredBases] = useState([]);
  const [loadingBases, setLoadingBases] = useState(false);

  // Base modal states
  // const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBase, setSelectedBase] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [baseToDelete, setBaseToDelete] = useState(null);

  // Functions for bases
  const fetchBases = async () => {
    try {
      setLoadingBases(true);

      const filters = {};
      if (selectedCompany) {
        filters.companyid = selectedCompany;
      }
      // Fetch all bases with no filters from the API
      const data = await getBases(filters);
      setBases(data);
      setFilteredBases(data);
    } catch (error) {
      console.error("Error fetching bases:", error);
      setBases([]);
      setFilteredBases([]);
    } finally {
      setLoadingBases(false);
    }
  };

  // Function to apply filters -- only search term is currently used
  const applyFilters = () => {
    try {
      const newFilteredBases = bases
        .filter((base) => {
          if (searchTerm && searchTerm.trim() !== "") {
            const search = searchTerm.toLowerCase().trim();

            // Check across all fields
            const matchesSearch =
              // Check baseid
              (base.baseid &&
                String(base.baseid).toLowerCase().includes(search)) ||
              // Check company
              (base.companies_id &&
                String(base.companies_id).toLowerCase().includes(search)) ||
              //Check sim card
              (base.simcard && base.simcard.toLowerCase().includes(search)) ||
              // Check simCardName
              (base.simCardName &&
                base.simCardName.toLowerCase().includes(search));

            return matchesSearch;
          }

          return true; // If no search term, return all
        })
        .sort((a, b) => a._id - b._id);

      // Only update filteredBases if there are changes
      setFilteredBases(newFilteredBases);
    } catch (error) {
      console.error("Error applying filters:", error);
      setFilteredBases([]);
    } finally {
      setLoadingBases(false);
    }
  };

  // Function to handle saving edited base
  const handleUpdateBase = async (baseData) => {
    try {
        console.log("Updating base with data:", baseData);
        console.log("Selected base:", selectedBase);
      const response = await updateBaseInDataBases(selectedBase._id, baseData);

      if (response.code === 200) {
        toast.success(t("base_updated_successfully"));
        setShowEditModal(false);
        setSelectedBase(null);
        fetchBases(); // Refresh the list after update
        return {
          success: true,
          message: t("base_updated_successfully"),
        };
      }
      return { success: false };
    } catch (error) {
      console.error("Error updating base:", error);
      toast.error(error.response?.data?.message || t("base_update_failed"));

      return {
        success: false,
        message: error.response?.data?.message || t("base_update_failed"),
      };
    }
  };

  // Function to handle deletion of a Base
  const handleDeleteSimCard = async (baseId) => {
    try {
      const response = await deleteBase(baseId);

      if (response.code === 200) {
        return {
          success: true,
          code: response.code,
        };
      }
    } catch (error) {
      console.error("Error deleting Base:", error);
      const errorData = error.response?.data;
      const errorCode = errorData?.errorCode || "UNKNOWN_ERROR";

      return {
        success: false,
        errorCode,
        error: errorData?.message || t("errorDeletingSimCard"),
        errorField: errorData?.field,
        errorValue: errorData?.value,
      };
    }
  };

  const handleConfirmDelete = async () => {
    if (baseToDelete) {
      const response = await handleDeleteSimCard(baseToDelete._id);

      // If deletion was successful, refresh the list
      if (response && response.code === 200) {
        toast.success(t("base_deleted_successfully"));
        setShowDeleteConfirmation(false);
        fetchBases(); // Refresh the list after deletion
      } else {
        toast.error(t("sim_card_delete_error"));
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setBaseToDelete(null);
  };

  // Handle action clicks
  const handleActionClick = (actionType, base) => {
    switch (actionType) {
      case "edit":
        setSelectedBase(base);
        setShowEditModal(true);
        break;
      case "delete":
        setBaseToDelete(base);
        setShowDeleteConfirmation(true);
        break;
      default:
        break;
    }
  };

  // Effects
  useEffect(() => {
    fetchBases();
  }, [selectedCompany, currentPage, recordsPerPage]);

  useEffect(() => {
    // Reset to page 1 whenever recordsPerPage changes
    setCurrentPage(1);
  }, [recordsPerPage]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, bases]);

  // Calculate pagination
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedBases = filteredBases.slice(startIndex, endIndex);

  return {
    // States
    companies,
    companiesLoading,
    searchTerm,
    setSearchTerm,
    selectedCompany,
    setSelectedCompany,
    // Pagination states
    currentPage,
    setCurrentPage,
    recordsPerPage,
    setRecordsPerPage,
    paginatedBases,
    // Bases states
    loadingBases,
    selectedBase,
    setSelectedBase,
    baseToDelete,
    setBaseToDelete,
    filteredBases,
    // Modals
    showEditModal,
    setShowEditModal,
    showDeleteConfirmation,
    setShowDeleteConfirmation,

    // Functions
    handleUpdateBase,
    handleConfirmDelete,
    handleCancelDelete,
    handleActionClick,
  };
};
